import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { RegistrationValidationPort } from '../../../domain/commerce-registry/ports/registration-validation.port';
import type { RegistrationValidationResult } from '../../../domain/commerce-registry/models/registration-validation.model';
import { ENVIRONMENT } from '../../../../environments/environment';

/** Legacy API response shape (saas). */
interface ValidacionCedulaApiResponse {
  code: number;
  message?: string;
  data?: unknown;
}

/** Aligns with lealcoinswebapp CodigoRespuesta. */
const CODE_OK = 100;
const CODE_NOT_VALID = 103;
const CODE_ALREADY_REGISTERED = 108;

@Injectable()
export class RegistrationValidationApiAdapter extends RegistrationValidationPort {
  constructor(private readonly http: HttpClient) {
    super();
  }

  validateCedula(params: {
    cedula: string;
    tipoDocumento: string;
    idComercio?: number;
  }): Observable<RegistrationValidationResult> {
    const base = ENVIRONMENT.apiMobileUrl?.trim();
    if (!base) {
      return of<RegistrationValidationResult>({
        outcome: 'failure',
        message: 'Servicio de validación no configurado',
      });
    }

    let httpParams = new HttpParams()
      .set('cedula', params.cedula.trim())
      .set('tipoDocumento', String(params.tipoDocumento).trim());
    if (params.idComercio != null && !Number.isNaN(params.idComercio)) {
      httpParams = httpParams.set('id_comercio', String(params.idComercio));
    }

    const url = `${base.replace(/\/$/, '')}/api/usuarios/v3/validacion_cedula`;
    return this.http.get<ValidacionCedulaApiResponse>(url, { params: httpParams }).pipe(
      map((res) => this.mapResponse(res)),
    );
  }

  private mapResponse(res: ValidacionCedulaApiResponse): RegistrationValidationResult {
    const code = res?.code;
    const message = typeof res?.message === 'string' ? res.message : '';

    if (code === CODE_OK) {
      return { outcome: 'not_registered' };
    }
    if (code === CODE_ALREADY_REGISTERED) {
      const uidFromData = typeof res?.data === 'string' ? res.data.trim() : '';
      return {
        outcome: 'already_registered',
        message: message || undefined,
        existingUserUid: uidFromData || undefined,
      };
    }
    if (code === CODE_NOT_VALID) {
      return { outcome: 'invalid_request', message: message || 'Solicitud no válida' };
    }
    return {
      outcome: 'failure',
      message: message || `Respuesta inesperada (${code ?? 'sin código'})`,
    };
  }
}
